import { isPlatformBrowser } from '@angular/common';
import { Directive, ElementRef, HostListener, PLATFORM_ID, Renderer2, afterNextRender, inject } from '@angular/core';

import { gsap } from 'gsap';

/**
 * Hover "slot machine" roll for a link label.
 *
 * WHY THIS BUILDS ITS DOM IN `afterNextRender` AND NOT `ngOnInit`.
 *
 * It wipes `innerHTML` and rebuilds the element's children. Angular's view
 * holds a reference to the original text node, so doing that during `ngOnInit`
 * destroys a node the framework still believes it owns — harmless while the
 * client threw the server's DOM away anyway, and NOT harmless once hydration
 * is on, because hydration claims that exact node first.
 *
 * `afterNextRender` runs browser-only and strictly after the first render has
 * settled, so hydration matches the server's markup and only then does the
 * decoration get applied. The effect is hover-only, so a frame's delay costs
 * nothing visible.
 *
 * It also used to run ON THE SERVER — the old guard read
 * `isPlatformBrowser(...) && matchMedia(...)`, which short-circuits to false
 * server-side, so SSR shipped the full rebuilt structure including a duplicate
 * `aria-hidden` copy of every label (46 extra nodes on /contact alone). The
 * server now ships the plain link and the browser decorates it.
 */
@Directive({
  selector: '[cntCinematicRoll]',
  standalone: true
})
export class CinematicRollDirective {
  private elementRef = inject(ElementRef);
  private renderer = inject(Renderer2);
  private platformId = inject(PLATFORM_ID);

  private el: HTMLElement;
  private innerWrap!: HTMLElement;
  private front!: HTMLElement;
  private back!: HTMLElement;
  private underline!: HTMLElement;

  constructor() {
    this.el = this.elementRef.nativeElement;
    // Browser-only and post-hydration. `afterNextRender` never runs on the
    // server, so the platform check below is only about the device.
    afterNextRender(() => this.decorate());
  }

  private decorate(): void {
    // Touch devices can never fire mouseenter, so the roll would only cost DOM
    // work on every page (wipe, four nodes, ~20 inline styles per link). Same
    // query app.component uses to skip the custom cursor.
    // The links keep their own layout: navbar links sit in a flex .nav-menu,
    // home-locations links carry `block`, footer links carry `inline-flex`.
    // (typeof check: jsdom in unit tests has no matchMedia.)
    if (
      !isPlatformBrowser(this.platformId) ||
      (typeof window.matchMedia === 'function' &&
        window.matchMedia('(hover: none), (pointer: coarse)').matches)
    ) return;

    // Safely extract HTML payload natively wrapping any deep nested icon structures seamlessly
    const contentPayload = this.el.innerHTML || '';

    // Bail before touching anything if there is nothing to clone yet.
    //
    // This directive only works with STATIC content. It reads innerHTML and
    // then replaces it, so anything Angular re-renders later — an
    // {{ interpolation }}, an @if, an async pipe — would be written back over
    // the rebuilt structure or wiped with nothing to restore it, leaving a
    // silently empty link: no error, no warning, just a link with no text.
    // (Running in afterNextRender means interpolation HAS resolved by now, so
    // this guard no longer fires for that reason — but the constraint on
    // dynamic content still holds, which is why it stays.)
    //
    // Leaving the element completely untouched costs it the hover effect but
    // keeps its content, which is strictly better than destroying it.
    if (!contentPayload.trim()) return;

    // Preserve existing geometry but constrain boundaries physically for the clipping mask
    const isBlock = this.el.classList.contains('block');
    this.renderer.setStyle(this.el, 'position', 'relative');
    this.renderer.setStyle(this.el, 'display', isBlock ? 'block' : 'inline-flex');
    this.renderer.setStyle(this.el, 'overflow', 'hidden');
    this.renderer.setStyle(this.el, 'vertical-align', 'middle');

    // Completely wipe static DOM payload to rebuild the physics slot-machine wrapper structurally
    this.el.innerHTML = '';

    this.innerWrap = this.renderer.createElement('span');
    this.renderer.setStyle(this.innerWrap, 'position', 'relative');
    this.renderer.setStyle(this.innerWrap, 'display', 'inline-flex');
    this.renderer.setStyle(this.innerWrap, 'flex-direction', 'column');
    
    this.front = this.renderer.createElement('span');
    this.front.innerHTML = contentPayload;
    this.renderer.setStyle(this.front, 'display', 'inline-block');
    this.renderer.setStyle(this.front, 'will-change', 'transform, opacity');
    
    this.back = this.renderer.createElement('span');
    this.back.innerHTML = contentPayload;
    // The roll effect needs a second copy of the label to slide in, but that
    // copy is decorative. opacity:0 does NOT remove it from the accessibility
    // tree, so without this every decorated link was announced twice.
    this.renderer.setAttribute(this.back, 'aria-hidden', 'true');
    this.renderer.setStyle(this.back, 'position', 'absolute');
    this.renderer.setStyle(this.back, 'top', '100%');
    this.renderer.setStyle(this.back, 'left', '0');
    this.renderer.setStyle(this.back, 'display', 'inline-block');
    this.renderer.setStyle(this.back, 'will-change', 'transform, opacity');
    this.renderer.setStyle(this.back, 'opacity', '0'); // start hidden
    
    // Construct Organic Vector Draw (Underline)
    this.underline = this.renderer.createElement('div');
    this.renderer.setStyle(this.underline, 'position', 'absolute');
    this.renderer.setStyle(this.underline, 'bottom', '0px');
    // Offset strictly slightly outside the text boundary
    this.renderer.setStyle(this.underline, 'left', '-2px');
    this.renderer.setStyle(this.underline, 'width', 'calc(100% + 4px)');
    this.renderer.setStyle(this.underline, 'height', '2px');
    this.renderer.setStyle(this.underline, 'border-radius', '2px');
    
    // Contextual Color Switching
    const isFooterWhite = this.el.classList.contains('text-white');
    if (isFooterWhite) {
      this.renderer.setStyle(this.underline, 'background-color', '#ffffff');
    } else {
      this.renderer.setStyle(this.underline, 'background-color', '#DC5B27');
    }
    
    this.renderer.setStyle(this.underline, 'transform-origin', 'right center');
    this.renderer.setStyle(this.underline, 'transform', 'scaleX(0)');
    
    // Bind into the DOM
    this.renderer.appendChild(this.innerWrap, this.front);
    this.renderer.appendChild(this.innerWrap, this.back);
    // Append underline INSIDE innerWrap to bound exactly to the text dimensions instead of the column block!
    this.renderer.appendChild(this.innerWrap, this.underline); 
    this.renderer.appendChild(this.el, this.innerWrap);
  }

  @HostListener('mouseenter')
  onMouseEnter() {
    // decorate() bails on empty content and on touch, in which case none of
    // the layers exist.
    if (!this.front) return;

    // Hardware accelerated slot machine rolling physics
    gsap.to(this.front, { yPercent: -100, opacity: 0, duration: 0.45, ease: 'power4.inOut' });
    gsap.to(this.back, { top: '0%', opacity: 1, duration: 0.45, ease: 'power4.inOut' });
    
    // Draw organic underline left-to-right
    gsap.fromTo(this.underline, 
      { transformOrigin: 'left center', scaleX: 0 },
      { scaleX: 1, duration: 0.4, ease: 'power3.out' }
    );
  }

  @HostListener('mouseleave')
  onMouseLeave() {
    if (!this.front) return;

    // Reverse-matrix snap back
    gsap.to(this.front, { yPercent: 0, opacity: 1, duration: 0.45, ease: 'power4.inOut' });
    gsap.to(this.back, { top: '100%', opacity: 0, duration: 0.45, ease: 'power4.inOut' });
    
    // Retract underline left-to-right
    gsap.fromTo(this.underline, 
      { transformOrigin: 'right center', scaleX: 1 },
      { scaleX: 0, duration: 0.4, ease: 'power3.in' }
    );
  }
}
